/**
 * Chrome / Firefox / Edge web push via Firebase Cloud Messaging.
 * Requires HTTPS (or localhost), a service worker, and BUDGET_FIREBASE_VAPID_KEY.
 */

import AsyncStorage from '@react-native-async-storage/async-storage';
import {
  getMessaging,
  getToken,
  isSupported,
  onMessage,
  type Messaging,
} from 'firebase/messaging';

import { FIREBASE_VAPID_KEY } from '../config/environment';
import { firebaseApp } from '../config/firebase.web';
import { ApiError, apiRequest } from './apiClient';
import type {
  PushPermissionStatus,
  RegisterDeviceResponse,
  RemotePushMessage,
  ShowWhisperPayload,
} from './pushNotificationTypes';

const LAST_REGISTERED_TOKEN_KEY = 'budget_last_registered_token';
const FCM_TOKEN_KEY = 'budget_fcm_token';
const PUSH_OPT_IN_KEY = 'budget_push_opt_in';
const SW_PATH = '/firebase-messaging-sw.js';

class PushNotificationService {
  private static instance: PushNotificationService;
  public onNotificationTap: ((remoteMessage: RemotePushMessage) => void) | null = null;
  private showWhisperCallback: ((data: ShowWhisperPayload) => void) | null = null;
  private deviceToken: string | null = null;
  private lastRegisteredToken: string | null = null;
  private isInitialized = false;
  private initializePromise: Promise<void> | null = null;
  private permissionRequestPromise: Promise<boolean> | null = null;
  private messaging: Messaging | null = null;
  private pushOptedIn = true;
  private tokenRefreshHandler: (() => void) | null = null;

  private constructor() {}

  public static getInstance(): PushNotificationService {
    if (!PushNotificationService.instance) {
      PushNotificationService.instance = new PushNotificationService();
    }
    return PushNotificationService.instance;
  }

  public async initialize(): Promise<void> {
    if (this.isInitialized) return;
    if (!this.initializePromise) {
      this.initializePromise = this.performInitialize();
    }
    await this.initializePromise;
  }

  private async performInitialize(): Promise<void> {
    try {
      this.lastRegisteredToken = await AsyncStorage.getItem(LAST_REGISTERED_TOKEN_KEY);
      const optIn = await AsyncStorage.getItem(PUSH_OPT_IN_KEY);
      this.pushOptedIn = optIn !== 'false';

      const supported = typeof window !== 'undefined' && (await isSupported());
      if (!supported) {
        console.log('[PushNotificationService] Web push not supported in this browser');
        this.isInitialized = true;
        return;
      }

      this.messaging = getMessaging(firebaseApp);
      onMessage(this.messaging, (payload) => {
        const title =
          payload.notification?.title ||
          (typeof payload.data?.title === 'string' ? payload.data.title : 'Notification');
        const body =
          payload.notification?.body ||
          (typeof payload.data?.body === 'string' ? payload.data.body : '');
        this.processForegroundNotification({
          messageId: payload.messageId,
          notification: { title, body },
          data: payload.data as Record<string, unknown> | undefined,
          sentTime: Date.now(),
        });
      });

      if (this.pushOptedIn && this.browserPermission() === 'granted') {
        await this.collectTokens();
      }

      this.isInitialized = true;
      console.log('[PushNotificationService] Web push initialized', {
        hasToken: !!this.deviceToken,
        permission: this.browserPermission(),
      });
    } catch (error) {
      console.error('[PushNotificationService] Web initialize failed', error);
      this.isInitialized = true;
    }
  }

  public async requestPermissionIfNeeded(options?: { force?: boolean }): Promise<boolean> {
    if (this.permissionRequestPromise) {
      return this.permissionRequestPromise;
    }
    this.permissionRequestPromise = this.performPermissionRequest(Boolean(options?.force));
    try {
      return await this.permissionRequestPromise;
    } finally {
      this.permissionRequestPromise = null;
    }
  }

  private async performPermissionRequest(_force: boolean): Promise<boolean> {
    if (typeof Notification === 'undefined') return false;
    const current = this.browserPermission();
    if (current === 'denied') return false;

    let granted = current === 'granted';
    if (!granted) {
      const result = await Notification.requestPermission();
      granted = result === 'granted';
    }
    if (!granted) return false;

    await this.setPushOptedIn(true);
    await this.collectTokens();
    return !!this.deviceToken || granted;
  }

  private async collectTokens(): Promise<void> {
    if (!this.messaging) {
      const supported = await isSupported();
      if (!supported) return;
      this.messaging = getMessaging(firebaseApp);
    }
    if (!FIREBASE_VAPID_KEY) {
      console.warn(
        '[PushNotificationService] BUDGET_FIREBASE_VAPID_KEY is missing — Chrome push token unavailable',
      );
      return;
    }
    try {
      const registration = await navigator.serviceWorker.register(SW_PATH);
      await navigator.serviceWorker.ready;
      const token = await getToken(this.messaging, {
        vapidKey: FIREBASE_VAPID_KEY,
        serviceWorkerRegistration: registration,
      });
      if (token) {
        this.deviceToken = token;
        await AsyncStorage.setItem(FCM_TOKEN_KEY, token);
        console.log(
          '[PushNotificationService] ✅ Web FCM token (Firebase Console → Messaging → Send test message)',
          token,
        );
      } else {
        console.warn('[PushNotificationService] getToken returned empty');
      }
    } catch (error) {
      console.error('[PushNotificationService] Web getToken failed', error);
    }
  }

  public getDeviceToken(): string | null {
    return this.deviceToken;
  }

  public getAPNSToken(): string | null {
    return null;
  }

  public hasRegisterableToken(): boolean {
    return !!this.deviceToken;
  }

  public async refreshAPNSToken(): Promise<boolean> {
    return false;
  }

  public async saveTokenToBackend(): Promise<boolean> {
    if (!this.deviceToken) {
      console.warn('[PushNotificationService] No web FCM token — cannot register');
      return false;
    }
    const tryRegister = async (platform: 'web' | 'android') => {
      await apiRequest<RegisterDeviceResponse>('/notifications/register-device', {
        method: 'POST',
        body: JSON.stringify({ platform, deviceToken: this.deviceToken }),
      });
    };
    try {
      try {
        await tryRegister('web');
      } catch (error) {
        // Live API may not have `web` yet — same FCM project tokens register as Android/SNS.
        const status = error instanceof ApiError ? error.statusCode : undefined;
        if (status !== 400 && status !== 500) throw error;
        console.warn(
          '[PushNotificationService] web platform rejected — registering as android so the token is stored',
          error instanceof Error ? error.message : error,
        );
        await tryRegister('android');
      }
      this.lastRegisteredToken = this.deviceToken;
      await AsyncStorage.setItem(LAST_REGISTERED_TOKEN_KEY, this.deviceToken);
      console.log('[PushNotificationService] ✅ Web device registered with budget-server');
      return true;
    } catch (error) {
      console.error('[PushNotificationService] Failed to register web device', error);
      return false;
    }
  }

  public async unregisterFromBackend(): Promise<boolean> {
    if (!this.deviceToken) return false;
    const tryUnreg = async (platform: 'web' | 'android') => {
      await apiRequest('/notifications/unregister-device', {
        method: 'POST',
        body: JSON.stringify({ platform, deviceToken: this.deviceToken }),
      });
    };
    try {
      try {
        await tryUnreg('web');
      } catch {
        await tryUnreg('android');
      }
      this.lastRegisteredToken = null;
      await AsyncStorage.removeItem(LAST_REGISTERED_TOKEN_KEY);
      return true;
    } catch (error) {
      console.error('[PushNotificationService] Failed to unregister web device', error);
      return false;
    }
  }

  public onTokenRefresh(callback: () => void): void {
    this.tokenRefreshHandler = callback;
  }

  public async hasTokenChanged(): Promise<boolean> {
    if (!this.deviceToken) return false;
    const lastRegistered = await AsyncStorage.getItem(LAST_REGISTERED_TOKEN_KEY);
    return this.deviceToken !== lastRegistered;
  }

  public getLastRegisteredToken(): string | null {
    return this.lastRegisteredToken;
  }

  public setOnNotificationTap(callback: (remoteMessage: RemotePushMessage) => void): void {
    this.onNotificationTap = callback;
  }

  public setShowWhisper(callback: (data: ShowWhisperPayload) => void): void {
    this.showWhisperCallback = callback;
  }

  public async getStoredToken(): Promise<string | null> {
    try {
      return await AsyncStorage.getItem(FCM_TOKEN_KEY);
    } catch {
      return null;
    }
  }

  public async clearStoredToken(): Promise<void> {
    this.deviceToken = null;
    await AsyncStorage.removeItem(FCM_TOKEN_KEY);
  }

  public async subscribeToTopic(_topic: string): Promise<boolean> {
    return false;
  }

  public async unsubscribeFromTopic(_topic: string): Promise<boolean> {
    return false;
  }

  public async areNotificationsEnabled(): Promise<boolean> {
    return this.browserPermission() === 'granted';
  }

  public async getPermissionStatus(): Promise<PushPermissionStatus> {
    if (typeof Notification === 'undefined') return 'unavailable';
    const permission = this.browserPermission();
    if (permission === 'granted') return 'granted';
    if (permission === 'denied') return 'blocked';
    return 'undetermined';
  }

  public async openSystemNotificationSettings(): Promise<void> {
    // Browsers do not allow opening notification settings programmatically.
  }

  public isPushOptedIn(): boolean {
    return this.pushOptedIn;
  }

  public async setPushOptedIn(value: boolean): Promise<void> {
    this.pushOptedIn = value;
    await AsyncStorage.setItem(PUSH_OPT_IN_KEY, value ? 'true' : 'false');
  }

  private browserPermission(): NotificationPermission | 'unsupported' {
    if (typeof Notification === 'undefined') return 'unsupported';
    return Notification.permission;
  }

  private processForegroundNotification(remoteMessage: RemotePushMessage): void {
    const title = remoteMessage.notification?.title || 'Notification';
    const body = remoteMessage.notification?.body || '';
    if (!title || !body) return;
    this.showWhisperCallback?.({
      title,
      message: body,
      onPress: () => {
        this.onNotificationTap?.(remoteMessage);
      },
    });
  }
}

export default PushNotificationService;
